import { Box, Sheet, Stack } from "@mui/joy";
import MessageInput from "../../../components/chat/MessageInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useChatMessageListQuery,
  useChatMessageSendMutation,
  useUserMeQuery,
} from "../../../__generated__/types-and-hooks";
import ChatBubble, { ChatMessageProps } from ".//ChatBubble";
import UserAvatar from "../../../components/UserAvatar";
import { useLive } from "../../../hooks/useLive";

export interface ChatProps {
  entityType: string;
  entityId: string;
}

export default function Chat({ entityId, entityType }: ChatProps) {
  const userMeQuery = useUserMeQuery();
  const userMe = userMeQuery.data?.user_me;

  const [textAreaValue, setTextAreaValue] = useState("");
  const chatMessageListQuery = useChatMessageListQuery({
    variables: { entityId, entityType, limit: 20 },
    fetchPolicy: "no-cache",
  });
  const [chatMessageSendMutation] = useChatMessageSendMutation({});
  const [chatMessages, setChatMessages] = useState<ChatMessageProps[]>([]);

  useEffect(() => {
    setChatMessages([]);
  }, [entityId, entityType]);

  const live$ = useLive();
  const messages$ = useMemo(
    () => live$.chat$(entityId, entityType),
    [entityId, entityType, live$]
  );

  useEffect(() => {
    if (chatMessageListQuery.data?.chat_message_list) {
      const messageList = chatMessageListQuery.data?.chat_message_list.messages
        .map((msg) => ({
          id: msg.id,
          content: msg.content,
          contentType: msg.contentType,
          timestamp: new Date(msg.createdAt),
          createdBy: msg.createdBy,
        }))
        .reverse();
      setChatMessages((msgs) => [...messageList, ...msgs]);
    }
  }, [chatMessageListQuery, entityId, entityType]);

  useEffect(() => {
    const sub = messages$.subscribe((event) => {
      const eventData: Record<string, string> = event.data.reduce(
        (acc, { key, value }) => ({ ...acc, [key]: value }),
        {}
      );

      const chatMessage = {
        id: eventData.id ?? `${chatMessages.length + 1}`,
        content: eventData?.content,
        contentType: eventData?.contentType,
        timestamp: new Date(event.committedAt),
        createdBy: event.committedBy,
      };
      console.log(chatMessage);
      setChatMessages((prev) => [...prev, chatMessage]);
    });
    return () => sub?.unsubscribe();
  }, [chatMessageListQuery, chatMessages, messages$]);

  const handleMessageSend = useCallback(() => {
    chatMessageSendMutation({
      variables: {
        message: {
          entityId,
          entityType,
          content: textAreaValue,
          contentType: "text",
          ack: true,
        },
      },
    });
  }, [textAreaValue, chatMessageSendMutation, entityId, entityType]);

  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (sheetRef && sheetRef.current !== null) {
        sheetRef.current.scrollTo({
          left: 0,
          top: sheetRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  }, [sheetRef, chatMessages]);

  return (
    <Box
      sx={{
        borderRadius: 6,
        height: "100%",
        m: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Sheet
        sx={{
          borderRadius: 0,
          m: 0,
          gap: 0,
          p: 0,
          flex: 1,
          overflowY: "auto",
          scrollBehavior: "smooth",
          scrollbarWidth: "thin",
        }}
        ref={sheetRef}
      >
        <Box sx={{ position: "absolute", width: "100%", p: 2, m: 0 }}>
          {chatMessages.map((message: ChatMessageProps) => {
            const isYou = message.createdBy === userMe?.id;
            return (
              <Stack
                key={message.id}
                direction="row"
                spacing={2}
                sx={{ flexDirection: isYou ? "row-reverse" : "row" }}
              >
                <Box sx={{ pt: 4 }}>
                  <UserAvatar userId={message.createdBy} size="sm" />
                </Box>
                <ChatBubble
                  variant={isYou ? "sent" : "received"}
                  {...message}
                />
              </Stack>
            );
          })}
        </Box>
      </Sheet>
      <MessageInput
        textAreaValue={textAreaValue}
        setTextAreaValue={setTextAreaValue}
        onSubmit={handleMessageSend}
      />
    </Box>
  );
}
